import React, { useRef, useState, useEffect } from "react";
import { sendMessage, getConversation } from "../services/messageServices";
import customerApi from "../api/CustomerApi";
import createEcho from "../utils/echo";

const LiveChat = () => {
  const [message, setMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [error, setError] = useState("");

  const [customerId, setCustomerId] = useState(null);

  const chatBodyRef = useRef(null);
  const fileInputRef = useRef(null);

  // ✅ logged-in customer
  const sender_id = customerId;
  const sender_type = "customer";

  // ✅ fixed admin
  const receiver_id = 1;
  const receiver_type = "admin";

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const loadLoggedInCustomer = async () => {
    try {
      setLoadingUser(true);
      setError("");

      const res = await customerApi.get("/customer/me");

      // depending on your API response shape
      const customer =
        res?.data?.customer ||
        res?.data?.data ||
        res?.data?.user ||
        res?.data;

      if (!customer?.id) {
        setError("Logged in customer not found.");
        return;
      }

      setCustomerId(Number(customer.id));
    } catch (err) {
      console.error("Load customer error:", err?.response?.data || err);
      setError("Failed to load logged in customer.");
    } finally {
      setLoadingUser(false);
    }
  };

  const loadMessages = async (currentCustomerId) => {
    if (!currentCustomerId) return;

    try {
      setLoadingMessages(true);

      const res = await getConversation({
        sender_id: currentCustomerId,
        sender_type,
        receiver_id,
        receiver_type,
      });

      const formatted = (res?.data || []).map((msg) => ({
        id: msg.id,
        sender: msg.sender_type === "admin" ? "admin" : "user",
        text: msg.message || "",
        time: formatTime(msg.created_at),
        image_url: msg.image_url || null,
      }));

      setMessages(formatted);
    } catch (err) {
      console.error("Load messages error:", err?.response?.data || err);
      setError("Failed to load messages.");
    } finally {
      setLoadingMessages(false);
    }
  };

  const resetComposer = () => {
    setMessage("");
    setSelectedImage(null);

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file.");
      return;
    }

    setError("");

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedImage(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const extractBackendError = (err) => {
    const data = err?.response?.data;

    if (data?.errors) {
      const firstError = Object.values(data.errors).flat()[0];
      return firstError || "Validation failed.";
    }

    return data?.message || "Message send failed. Please try again.";
  };

  const handleSendMessage = async () => {
    const trimmedMessage = message.trim();

    if ((!trimmedMessage && !selectedImage) || sending || !sender_id) return;

    setError("");

    const tempMessage = {
      id: Date.now(),
      sender: "user",
      text: trimmedMessage,
      time: getCurrentTime(),
      image_url: previewUrl || null,
      isTemp: true,
    };

    setMessages((prev) => [...prev, tempMessage]);

    const sendingMessage = trimmedMessage;
    const sendingImage = selectedImage;

    resetComposer();
    setSending(true);

    try {
      const response = await sendMessage({
        sender_id,
        sender_type,
        receiver_id,
        receiver_type,
        message: sendingMessage,
        image: sendingImage,
      });

      const savedMessage = response?.data;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempMessage.id
            ? {
                id: savedMessage?.id || tempMessage.id,
                sender: savedMessage?.sender_type === "admin" ? "admin" : "user",
                text: savedMessage?.message || sendingMessage || "",
                time: formatTime(savedMessage?.created_at) || getCurrentTime(),
                image_url: savedMessage?.image_url || null,
              }
            : msg
        )
      );
    } catch (err) {
      console.error("Send message error:", err?.response?.data || err);

      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id));
      setMessage(sendingMessage);
      setSelectedImage(sendingImage);

      if (sendingImage) {
        const restoredPreview = URL.createObjectURL(sendingImage);
        setPreviewUrl(restoredPreview);
      }

      setError(extractBackendError(err));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    loadLoggedInCustomer();
  }, []);

  useEffect(() => {

    if (!customerId) {
      return;
    }


    loadMessages(customerId);

    // Create Echo instance with current token
    const echo = createEcho();


    // Subscribe to private channel
    const channel = echo.private(`chat.${customerId}`);
    channel.listen(".message.sent", (data) => {
      const incoming = {
        id: data.id,
        sender: data.sender_type === "admin" ? "admin" : "user",
        text: data.message || "",
        time: formatTime(data.created_at),
        image_url: data.image_url || null,
      };

      setMessages((prev) => {
        // Avoid duplicate if already exists
        const isDuplicate = prev.some(
          (m) => !m.isTemp && m.id === incoming.id
        );
        if (isDuplicate) return prev;

        // Replace temp optimistic message if it matches
        const hasTempMatch = prev.some(
          (m) => m.isTemp && m.text === incoming.text && m.sender === "user"
        );
        if (hasTempMatch) {
          return prev.map((m) =>
            m.isTemp && m.text === incoming.text ? incoming : m
          );
        }

        return [...prev, incoming];
      });
    });

    // Cleanup on unmount
    return () => {
      echo.leave(`chat.${customerId}`);
      echo.disconnect();
    };
  }, [customerId]);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <section className="live-chat-page py-80">
      <style>{`
        .chat-wrapper {
          max-width: 900px;
          margin: 0 auto;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
        }

        .chat-header {
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .chat-header h4 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .chat-body {
          padding: 24px;
          min-height: 500px;
          max-height: 500px;
          overflow-y: auto;
          background: #f8fafc;
        }

        .message-row {
          display: flex;
          margin-bottom: 16px;
        }

        .message-row.admin {
          justify-content: flex-start;
        }

        .message-row.user {
          justify-content: flex-end;
        }

        .message-bubble-wrap {
          max-width: 75%;
          display: flex;
          flex-direction: column;
        }

        .message-bubble {
          padding: 12px 16px;
          font-size: 15px;
          line-height: 1.6;
          border-radius: 16px;
          word-break: break-word;
        }

        .message-row.admin .message-bubble {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #111827;
        }

        .message-row.user .message-bubble {
          background: #2563eb;
          color: #fff;
        }

        .message-image {
          width: 100%;
          max-width: 220px;
          border-radius: 12px;
          display: block;
          margin-top: 10px;
          border: 1px solid rgba(0,0,0,0.08);
        }

        .message-time {
          font-size: 12px;
          color: #6b7280;
          margin-top: 6px;
        }

        .message-row.user .message-time {
          text-align: right;
        }

        .chat-input-area {
          padding: 16px 20px;
          border-top: 1px solid #e5e7eb;
          background: #fff;
        }

        .chat-preview {
          margin-bottom: 12px;
          position: relative;
          display: inline-block;
        }

        .chat-preview img {
          max-width: 140px;
          max-height: 140px;
          border-radius: 12px;
          border: 1px solid #d1d5db;
          display: block;
        }

        .remove-preview-btn {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 28px;
          height: 28px;
          border: none;
          border-radius: 50%;
          background: #111827;
          color: #fff;
          cursor: pointer;
          font-size: 14px;
          line-height: 28px;
          text-align: center;
        }

        .chat-input-box {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .attach-btn {
          width: 50px;
          height: 50px;
          border: 1px solid #d1d5db;
          border-radius: 12px;
          background: #fff;
          cursor: pointer;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .chat-input {
          flex: 1;
          height: 50px;
          border: 1px solid #d1d5db;
          border-radius: 12px;
          padding: 0 16px;
          outline: none;
          font-size: 14px;
        }

        .send-btn {
          height: 50px;
          padding: 0 20px;
          border: none;
          border-radius: 12px;
          background: #2563eb;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
        }

        .send-btn:disabled,
        .attach-btn:disabled {
          background: #f3f4f6;
          cursor: not-allowed;
          color: #9ca3af;
        }

        .chat-error {
          color: #dc2626;
          font-size: 13px;
          margin-top: 10px;
        }

        .chat-loading {
          text-align: center;
          color: #6b7280;
          padding: 40px 20px;
        }

        .hidden-file-input {
          display: none;
        }

        @media (max-width: 768px) {
          .chat-body {
            padding: 16px;
          }

          .message-bubble-wrap {
            max-width: 90%;
          }

          .chat-input-box {
            flex-wrap: wrap;
          }

          .chat-input {
            width: 100%;
          }

          .send-btn {
            width: 100%;
          }
        }
      `}</style>

      <div className="container container-lg">
        <div className="chat-wrapper">
          <div className="chat-header">
            <h4>Live Chat</h4>
          </div>

          <div className="chat-body" ref={chatBodyRef}>
            {loadingUser || loadingMessages ? (
              <div className="chat-loading">Loading chat...</div>
            ) : messages.length === 0 ? (
              <div className="chat-loading">No messages yet</div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`message-row ${msg.sender === "admin" ? "admin" : "user"}`}
                >
                  <div className="message-bubble-wrap">
                    <div className="message-bubble">
                      {msg.text ? <div>{msg.text}</div> : null}
                      {msg.image_url ? (
                        <img
                          src={msg.image_url}
                          alt="chat attachment"
                          className="message-image"
                        />
                      ) : null}
                    </div>
                    <div className="message-time">{msg.time}</div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="chat-input-area">
            {previewUrl ? (
              <div className="chat-preview">
                <img src={previewUrl} alt="preview" />
                <button
                  type="button"
                  className="remove-preview-btn"
                  onClick={removeSelectedImage}
                  disabled={sending}
                >
                  ×
                </button>
              </div>
            ) : null}

            <div className="chat-input-box">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden-file-input"
                onChange={handleImageChange}
                disabled={sending || !sender_id}
              />

              <button
                type="button"
                className="attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || !sender_id}
                title="Attach image"
              >
                📎
              </button>

              <input
                type="text"
                className="chat-input"
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending || !sender_id}
              />

              <button
                type="button"
                className="send-btn"
                onClick={handleSendMessage}
                disabled={(!message.trim() && !selectedImage) || sending || !sender_id}
              >
                {sending ? "Sending..." : "Send"}
              </button>
            </div>

            {error && <div className="chat-error">{error}</div>}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveChat;
